import { z } from "zod";
import { SuccessActionType } from "../constants";
import { IAccess } from "./access";

// User table sort types
export type SortField = "id" | "name" | "email" | "phoneNumber" | "pets";
export type SortDirection = "asc" | "desc";

// Define a proper type for filter options
export type FilterOption = "all" | "name" | "email" | "phoneNumber";

// Field wrapper props interface
export interface FieldWrapperProps {
  fieldName: string;
  disabled: boolean;
  onClick: (fieldName: string) => void;
  children: React.ReactNode;
}

/**
 * TablePagination props interface
 */
export interface TablePaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  startIndex: number;
  endIndex: number;
  goToPage: (page: number) => void;
  itemType?: string;
  access?: IAccess // Optional item type for display (e.g., "users", "subscriptions", "items")
  showPageNumbers?: boolean;
  shouldShowBorder?: boolean;
}

export enum UserAssetCategory {
  USER_PROFILE_IMG = "USER_PROFILE_IMG",
  PET_PROFILE_IMG = "PET_PROFILE_IMG",
  HEALTH_RECORD = "HEALTH_RECORD"
}

export const assetUploadRequestSchema = z.object({
  assetCategory: z.nativeEnum(UserAssetCategory),
  petId: z.string().optional(),
  healthRecordId: z.string().optional()
});

export type AssetUploadRequest = z.infer<typeof assetUploadRequestSchema>;

export const assetUploadResponseSchema = z.object({
  url: z.string(),
  key: z.string()
});

export type AssetUploadResponse = z.infer<typeof assetUploadResponseSchema>;

export const onboardingStepsSchema = z.record(z.string().optional());

export type OnboardingSteps = z.infer<typeof onboardingStepsSchema>;

export const onboardingDataSchema = z.object({
  isOnboarded: z.boolean(),
  onboardingSteps: onboardingStepsSchema,
  currentStep: z.string()
});

export type OnboardingData = z.infer<typeof onboardingDataSchema>;

export interface SubmitOptions {
  isEdit: boolean;
  memberId?: string;
  phoneNumberUpdated?: boolean;
  refetch?: () => void;
  setSuccessOpen: (state: boolean) => void;
}

export interface LoginFormInputs {
  email: string;
  password: string;
}

export interface MobileNumberInputProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  required?: boolean;
  className?: string;
  disabled?: boolean;
}

export interface SuccessDialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
  actionType?: SuccessActionType;
}

export interface FormActionsProps {
  onClose: () => void;
  isSubmitting: boolean;
  isEdit?: boolean;
  sticky?: boolean;
  cancelLabel?: string;
  createLabel?: string;
  updateLabel?: string;
  savingLabel?: string;
  submitLabel?: string;
  containerClassName?: string;
  submitClassName?: string;
  cancelClassName?: string;
  access?: IAccess;
}

// Text Editor Components Interfaces
export interface ITiptapEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export interface ITiptapEditorHandle {
  insertImage: (url: string, alt?: string) => void;
}

export interface IEditorContextMenuProps {
  x: number;
  y: number;
  onAction: (action: string) => void;
}

export interface IEditorButton {
  action: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  shortcut?: string;
  level?: number;
}

export enum EServiceCategory {
  WALKING = "WALKING",
  GROOMING = "GROOMING",
  TRAINING = "TRAINING",
  PRODUCTS = "PRODUCTS",
}

export enum EServiceType {
  SUBSCRIPTION = "SUBSCRIPTION",
  SINGLE = "ONE_TIME",
}

export enum EPartnerRole {
  WALKER = "Walker",
  GROOMER = "Groomer",
  TRAINER = "Trainer",
}

export interface ILabelValueOption {
  value: string;
  label: string;
}

// ============================================================================
// COMMON ZOD SCHEMAS
// ============================================================================

/**
 * UUID schema factory
 * @param errorMessage - Custom error message for invalid UUID
 * @returns Zod schema for UUID validation
 */
export const uuidSchema = (errorMessage = "Invalid UUID") => z.string().uuid(errorMessage);

/**
 * Partner ID schema (UUID with custom error message)
 */
export const partnerIdSchema = uuidSchema("Invalid partner");

/**
 * Year schema base (2000-2100)
 */
export const yearSchemaBase = z.number().int().min(2000).max(2100);

/**
 * Year schema (optional, with preprocessing)
 * @returns Zod schema that accepts number or string and converts to number
 */
export function yearSchema() {
  return z.preprocess(val => (val ? Number(val) : undefined), yearSchemaBase.nullish());
}

/**
 * Year schema (required, with preprocessing)
 * @returns Zod schema that accepts number or string and converts to number
 */
export function yearSchemaRequired() {
  return z.preprocess(val => Number(val), yearSchemaBase);
}

/**
 * Month schema base (1-12)
 */
export const monthSchemaBase = z.number().int().min(1).max(12);

/**
 * Month schema (optional, with preprocessing)
 * @returns Zod schema that accepts number or string and converts to number
 */
export function monthSchema() {
  return z.preprocess(val => (val ? Number(val) : undefined), monthSchemaBase.nullish());
}

/**
 * Month schema (required, with preprocessing)
 * @returns Zod schema that accepts number or string and converts to number
 */
export function monthSchemaRequired() {
  return z.preprocess(val => Number(val), monthSchemaBase);
}

/**
 * Date schema for YYYY-MM-DD format
 * Validates date strings in ISO date format (e.g., "2024-01-15")
 */
export const dateYYYYMMDDSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format");

const TIMESTAMP_ERROR_MESSAGE = "Timestamp must be a positive integer";

export const timestampSchema = z.number().int().positive(TIMESTAMP_ERROR_MESSAGE);

export const createdAtSchema = z.number().int().positive(TIMESTAMP_ERROR_MESSAGE);

export const updatedAtSchema = z.number().int().positive(TIMESTAMP_ERROR_MESSAGE);

export const approvedAtSchema = z.number().int().positive(TIMESTAMP_ERROR_MESSAGE);

export const createdBySchema = z.string();

export const updatedBySchema = z.string();